import styled from '@emotion/styled';
import Button from 'aquilla/core/molecules/Button';
import P1 from '@base/P1/P1';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import pushRoute from '@utils/pushRoute';
import { evtNameCategoryMap, getCourseEnrollMessage } from '@constants/index';
import { useSelector, useDispatch } from 'react-redux';
import usePurchaseCTAHandler from '@hooks/usePurchaseCTAHandler';
import { useSnackbar } from '@stores/snackbar';
import { segment } from '@actions/analytics';
import Dialog from '@comp/Dialog/Dialog';
import { enrollInBatch } from '@actions/batches';
import {
  CHECKOUT_STARTED,
  BATCH_UNENROLLED,
  BATCH_ENROLLED,
  BATCH_KEYS,
  BATCH_ENROLL_ATTEMPTED,
  BATCH_ENROLL_POPUP_CLICKED,
  BATCH_UNENROLL_ATTEMPTED,
  BATCH_MANAGE_BATCH,
  STORE_EXCLUSIVE_BUY_NOW_CLICKED
} from '@constants/segment';
import { useState, useCallback, useRef, useEffect } from 'react';
import useGoalEventProps from '@hooks/useGoalEventProps';
import useAllTypesOfCourseEventProps from '@hooks/useAllTypesOfCourseEventProps';
import PropTypes from 'prop-types';

import LiteUpgradeDialog from '@cont/Course/CourseHeader/LiteUpgradeDialog';
import useLiteUpgradeDialog from '@hooks/useLiteUpgradeDialog';
import {
  getPlusSubscriptionInfoLatest,
  getIsSubscribedToLite,
  getHasExpiredSubscriptionToGoal
} from '@selectors/subscription';
import useBatchDetails from '@api/hooks/batch/useBatchDetails';
import { useIsMobile } from '@stores/AppCommonData';
import { useSelectedGoalUID } from '@api/hooks/topology/useSelectedGoal';
import useFetch from '@hooks/useFetch';
import useOneLinkSmartScriptV2 from '@hooks/useOneLinkSmartScriptV2';
import useIsRecordedGoal from '@hooks/useIsRecordedGoal';
import Drawer from '@base/Drawer/Drawer';
import { CHECKOUT_PROCEED_TO_PAYMENT_SELECTED } from '@cont/Checkout/constants/segment';
import getPreservedQueryParams from '@utils/getPreservedQueryParams';
import { HOST_TYPE } from '@utils/api-helper/host';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import useShowLoginDrawerOnBatch from '@stores/showLoginDrawerOnBatch';
import BatchFeedbackDrawer, { drawerTypes } from './BatchFeedbackDrawer';
import BatchJoinConfirmationDrawer from './BatchJoinConfirmationDrawer';
import BatchJoinSuccessDrawer from './BatchJoinSuccessDrawer';
import {
  getBatchReferralInStorage,
  getSkuBasedEventProps,
  REDIRECT_CTA_CONSTANTS,
  getButtonLabel,
  getEndpoint
} from './utils';

const EnrollButton = styled(Button)`
  width: 210px;
  height: 48px;
  transition: all 300ms ease;
  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;

const StyP2 = styled(P1)`
  font-weight: ${Fonts.SEMIBOLD};
  line-height: 18px;
`;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacings.SPACING_6B};
  ${mediaQuery.uptoMobile} {
    width: ${({ $fullWidth }) => ($fullWidth ? '100%' : '210px')};
  }
`;

const StyDrawer = styled(Drawer)`
  &.MuiDrawer-root > .MuiPaper-root {
    background-color: var(--color-base-1);
  }
`;

const BatchEnroll = ({
  batchUID,
  isSticky = false,
  isPrimaryBatchEnabled = false,
  isEnrolAndShareButtonVisible = true,
  showStoreSku,
  showBuy,
  activeSku
}) => {
  const batchRef = useRef('');
  const dispatch = useDispatch();

  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);

  const { showLoginDrawer, loginDrawerCtaName, setShowLoginDrawer } =
    useShowLoginDrawerOnBatch();

  const { data = {}, mutate: refetchBatchCount } = useFetch(
    isPrimaryBatchEnabled
      ? 'v2/topology/users/TMUVD/self/?actions=[enrolled_batch_count]'
      : null,
    { hostType: HOST_TYPE.BACKEND_API_HOST }
  );
  const { enrolledBatchCount } = data;
  const hasMultipleBatches = enrolledBatchCount > 0;

  const { open: showSnackbar } = useSnackbar();

  const [unEnrollModalOpen, setUnEnrollModalOpen] = useState(false);
  const [drawerType, setDrawerType] = useState(null);

  const { batchDetails, mutate: refetchBatchData } = useBatchDetails(batchUID);
  const { goal, uid, isEnrolled, isPrimaryCentreBatch } = batchDetails || {};
  const isMobile = useIsMobile();
  const batchGoalUID = batchDetails?.goal?.uid;
  const isSubscribed = !showBuy;
  const CTALabel = usePurchaseCTAHandler(batchGoalUID);
  const {
    showLiteUpgradeDialog,
    closeLiteUpgradeDialog,
    openLiteUpgradeDialog
  } = useLiteUpgradeDialog();
  const isLiteUser = useSelector(getIsSubscribedToLite(batchGoalUID));
  const subscriptionExpired = useSelector(
    getHasExpiredSubscriptionToGoal(batchGoalUID)
  );
  const hasSubscription = useSelector(
    getPlusSubscriptionInfoLatest(batchGoalUID)
  );

  const onCloseModal = () => setUnEnrollModalOpen(false);
  const onCloseDrawer = () => {
    setDrawerType(null);
    if (
      drawerType === drawerTypes.JOIN_BATCH ||
      drawerType === drawerTypes.SUCCESS
    )
      refetchBatchData();
  };

  const goalSegmentData = useGoalEventProps({ batchGoalUID });
  const segmentData = useAllTypesOfCourseEventProps({
    course: batchDetails,
    isBatch: true
  });
  const appDownloadLink = useOneLinkSmartScriptV2({
    campaign: 'batch page'
  });
  const selectedGoalUID = useSelectedGoalUID();
  const { uid: goalUID, name: goalName } = batchDetails?.goal || {};
  const isRecordedGoal = useIsRecordedGoal(goalUID);

  useEffect(() => {
    if (isPrimaryBatchEnabled) refetchBatchCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPrimaryBatchEnabled]);

  // eslint-disable-next-line no-nested-ternary
  const purchaseType = hasSubscription
    ? hasSubscription.time_remaining
      ? 'extension'
      : 'renewal'
    : 'new';

  const toggleEnroll = useCallback(
    async (batchUid) => {
      if (isEnrolled) {
        batchRef.current = batchDetails;
        if (isPrimaryBatchEnabled) {
          setDrawerType(drawerTypes.LEAVE_BATCH);
          segment.track(BATCH_UNENROLL_ATTEMPTED, {
            ...goalSegmentData,
            ...segmentData
          });
        } else {
          setUnEnrollModalOpen(true);
        }
      } else {
        segment.track(
          BATCH_ENROLLED,
          {
            ...goalSegmentData,
            ...segmentData
          },
          evtNameCategoryMap.WITHOUT_CATEGORY,
          true,
          BATCH_KEYS
        );
        await dispatch(enrollInBatch(batchUid, !isEnrolled));
        if (
          !isPrimaryBatchEnabled ||
          (isPrimaryBatchEnabled && !hasMultipleBatches)
        ) {
          refetchBatchData();
          showSnackbar({
            message: getCourseEnrollMessage({
              type: isPrimaryBatchEnabled ? 'Classes' : 'Courses',
              goalUID,
              selectedGoalUID,
              goalName
            }),
            isRecordedGoal,
            type: isPrimaryBatchEnabled ? 'info' : 'success',
            anchorOrigin: isPrimaryBatchEnabled
              ? { vertical: 'bottom', horizontal: 'center' }
              : { vertical: 'top', horizontal: 'center' }
          });
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, batchDetails]
  );

  const handleRedirect = () => {
    if (isLiteUser) {
      openLiteUpgradeDialog();
      return;
    }
    if (isSticky && (isEnrolled || !isSubscribed)) {
      window.open(appDownloadLink, '_blank');
      return;
    }
    if (isSubscribed) {
      if (isPrimaryBatchEnabled && hasMultipleBatches && !isEnrolled) {
        segment.track(BATCH_ENROLL_ATTEMPTED, {
          ...goalSegmentData,
          ...segmentData
        });
        setDrawerType(drawerTypes.CONFIRMATION);
        return;
      }
      toggleEnroll(uid, isEnrolled);
    } else {
      let eventProps = {
        purchase_type: purchaseType,
        goal_uid: goal?.uid,
        goal_name: goal?.name,
        is_subscription_active: false,
        is_upgrade: false
      };

      if (showStoreSku) {
        eventProps = {
          ...eventProps,
          ...(getSkuBasedEventProps(activeSku, batchDetails) || {})
        };
      }
      segment.track(
        CHECKOUT_STARTED,
        { ...eventProps },
        evtNameCategoryMap.BOTH
      );
      if (showStoreSku) {
        segment.track(
          CHECKOUT_PROCEED_TO_PAYMENT_SELECTED,
          { ...eventProps },
          evtNameCategoryMap.BOTH
        );
      }
      if (showStoreSku && batchDetails?.isStoreExclusive) {
        segment.track(STORE_EXCLUSIVE_BUY_NOW_CLICKED, {
          entity_content_type: 'Batch',
          entity_title: batchDetails?.name,
          entity_uid: batchDetails?.uid,
          is_store_exclusive: batchDetails?.isStoreExclusive,
          available_for: activeSku?.availableFor,
          is_purchased: false,
          is_sellable: batchDetails?.isSellable,
          is_enrolled: batchDetails?.isEnrolled,
          start_date: batchDetails?.startsAt
        });
      }
      if (goal?.uid && showStoreSku) {
        const referralCode = getBatchReferralInStorage(batchUID);
        const queryParmas = getPreservedQueryParams({
          referral_code: encodeURIComponent(referralCode || '')
        });
        pushRoute(
          `/goal/[goalSlug]/[goalUID]/subscribe/[subscriptionUID]${queryParmas}`,
          `/goal/${goal.slug}/${goal.uid}/subscribe/${
            showStoreSku ? activeSku?.uid : ''
          }${queryParmas}`,
          {},
          'batch'
        );
      } else {
        pushRoute(
          `/goal/[goalSlug]/[goalUID]/subscribe/${getPreservedQueryParams()}`,
          `/goal/${goal.slug}/${
            goal.uid
          }/subscribe/${getPreservedQueryParams()}`,
          {},
          'batch'
        );
      }
    }
  };

  const handleEnroll = (e) => {
    if (!isLoggedIn) {
      setShowLoginDrawer(true, REDIRECT_CTA_CONSTANTS.GET_SUBSCRIPTION);
      openLoginDrawer();
    }

    if (isLoggedIn) {
      if (e) e.stopPropagation();
      handleRedirect();
    }
  };

  useEffect(() => {
    if (
      isLoggedIn &&
      showLoginDrawer &&
      loginDrawerCtaName === REDIRECT_CTA_CONSTANTS.GET_SUBSCRIPTION
    ) {
      setShowLoginDrawer(false, '');
      handleRedirect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const upgradeSubscription = (e) => {
    e.stopPropagation();
    if (goal?.uid)
      pushRoute(
        '/goal/[goalSlug]/[goalUID]/subscribe',
        `/goal/${goal.slug}/${goal.uid}/subscribe`,
        {},
        'batch'
      );
  };

  const acceptOnClick = async () => {
    segment.track(
      BATCH_UNENROLLED,
      {
        ...goalSegmentData,
        ...segmentData,
        batch_state: batchDetails.state,
        start_date: batchDetails.startsAt,
        end_date: batchDetails.completedAt
      },
      evtNameCategoryMap.WITHOUT_CATEGORY,
      true,
      BATCH_KEYS
    );
    setUnEnrollModalOpen(false);
    await dispatch(enrollInBatch(batchRef.current.uid, false));
    if (isPrimaryBatchEnabled) {
      showSnackbar({
        message: 'Classes will be removed from planner within a few minutes',
        type: 'info',
        anchorOrigin: { vertical: 'bottom', horizontal: 'center' }
      });
    }
    refetchBatchData();
  };

  const onCtaClick = async () => {
    if (drawerType === drawerTypes.LEAVE_BATCH) {
      acceptOnClick();
    } else {
      await toggleEnroll(uid, isEnrolled);
      setDrawerType(drawerTypes.SUCCESS);
    }
  };

  const onMultipleBatchJoinConfirmation = () => {
    segment.track(BATCH_ENROLL_POPUP_CLICKED, {
      ...goalSegmentData,
      ...segmentData
    });
    setDrawerType(drawerTypes.JOIN_BATCH);
  };

  const onManageBatch = () => {
    refetchBatchData();
    segment.track(BATCH_MANAGE_BATCH, {
      ...goalSegmentData,
      ...segmentData
    });
  };

  return (
    <>
      {showLiteUpgradeDialog && (
        <LiteUpgradeDialog
          onClose={closeLiteUpgradeDialog}
          upgradeSubscription={upgradeSubscription}
        />
      )}
      <Dialog
        onClose={onCloseModal}
        dialogState={unEnrollModalOpen}
        heading="Are you sure you want to unenroll?"
        message="All courses from this batch will be removed from your schedule and you will stop receiving updates related to this batch"
        acceptLabel="Unenroll"
        rejectLabel="Cancel"
        acceptOnClick={acceptOnClick}
        rejectOnClick={onCloseModal}
      />
      <Wrapper $fullWidth={!showStoreSku}>
        {isPrimaryBatchEnabled &&
        hasMultipleBatches &&
        !isEnrolled &&
        isEnrolAndShareButtonVisible &&
        isSubscribed ? (
          <StyP2 color="var(--color-text-primary)">
            You’re already learning from other batch
          </StyP2>
        ) : null}
        {isEnrolAndShareButtonVisible && !isPrimaryCentreBatch ? (
          <EnrollButton
            variant={isEnrolled && !isSticky ? 'secondary' : 'primary'}
            label={getButtonLabel(
              isSticky,
              isEnrolled,
              isSubscribed,
              isMobile,
              CTALabel,
              subscriptionExpired,
              isPrimaryBatchEnabled,
              showStoreSku
            )}
            size="medium"
            color="black"
            onClick={(e) => handleEnroll(e, isSticky)}
          />
        ) : null}
      </Wrapper>
      <StyDrawer
        drawerState={drawerType in drawerTypes}
        onClose={onCloseDrawer}
      >
        <BatchFeedbackDrawer
          open={
            drawerType === drawerTypes.LEAVE_BATCH ||
            drawerType === drawerTypes.JOIN_BATCH
          }
          onCtaClick={onCtaClick}
          ctaText={
            drawerType === drawerTypes.LEAVE_BATCH ? 'Leave batch' : 'Continue'
          }
          endpoint={getEndpoint(
            drawerType === drawerTypes.LEAVE_BATCH,
            batchUID
          )}
          batchUID={batchUID}
          drawerType={drawerType}
        />
        <BatchJoinConfirmationDrawer
          open={drawerType === drawerTypes.CONFIRMATION}
          onConfirmation={onMultipleBatchJoinConfirmation}
        />
        <BatchJoinSuccessDrawer
          open={drawerType === drawerTypes.SUCCESS}
          onManageBatch={onManageBatch}
        />
      </StyDrawer>
    </>
  );
};

export default BatchEnroll;

BatchEnroll.propTypes = {
  batchUID: PropTypes.string.isRequired,
  isSticky: PropTypes.bool
};
BatchEnroll.defaultProps = {
  isSticky: false
};
